/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useRecurrence.js
 *
 * Purpose: Manages per-sample/assay/panel test recurrence (repeat testing dates) state and
 *     derives create payloads incorporating those dates.
 *
 * Scope: recurring test scheduling within the order request test-selection flow.
 *
 * Tags: customer-portal, orders
 *
 * Overview: Keys each recurrence entry by a JSON-stringified [sampleId, assayId, panelId]
 *     tuple, storing entries in state gated by useRecurrenceParameters' isEnabled flag.
 *     Provides getRecurrence/setRecurrence/removeRecurrence to read, merge, and bulk-clear (via
 *     regex key matching) recurrence entries, checkEditing to detect whether a sample/panel id
 *     pair maps to an existing recurrence, and createPayload to expand a base test payload into
 *     one entry per recurrence date, dropping the original estimated start/complete date fields
 *     when dates are present.
 *
 * Exports: default useRecurrence hook; named defaultValue constant (the empty/default
 *     recurrence shape).
 */

import { useState } from 'react';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import useRecurrenceParameters from './useRecurrenceParameters';

export const defaultValue = {
  dates: [],
  shouldAutoFill: true,
  businessDaysOnly: true,
};

function getKey(sampleId, assayId, panelId) {
  if (!sampleId || !assayId) return;
  return JSON.stringify(
    panelId ? [sampleId, assayId, panelId] : [sampleId, assayId],
  );
}

export default function useRecurrence() {
  const { isEnabled } = useRecurrenceParameters();
  const [recurrence, addRecurrence] = useState({});

  function getRecurrence({ sampleId, assayId, panelId }) {
    if (!isEnabled) return defaultValue;
    return recurrence[getKey(sampleId, assayId, panelId)] || defaultValue;
  }

  function setRecurrence({ sampleId, assayId, panelId, ...value }) {
    if (!isEnabled) return;
    const key = getKey(sampleId, assayId, panelId);
    const current = getRecurrence({ sampleId, assayId, panelId });
    addRecurrence((prev) => ({
      ...prev,
      [key]: {
        ...current,
        ...value,
      },
    }));
  }

  function removeRecurrence({ sampleId, assayId, panelId }) {
    if (!isEnabled) return;

    let entRegex;
    if (assayId && panelId) {
      // Scoped to a specific assay within a specific panel.
      entRegex = `^\\[[1-9]+,${assayId},${panelId}\\]$`;
    } else if (sampleId) {
      entRegex = `/^\[${sampleId},[1-9]+(,[1-9]+)?\]$/`;
    } else if (assayId) {
      entRegex = `/^\[[1-9]+,${assayId},[1-9]+\]$/`;
    } else if (panelId) {
      entRegex = `/^\[[1-9]+,[1-9]+,${panelId}\]$/`;
    }
    const regex = new RegExp(entRegex);
    const newVal = omitBy(recurrence, (_, key) => !regex.test(key));
    addRecurrence(newVal);
  }

  function checkEditing({ id, sampleId, isPanel }) {
    return (
      Object.keys(recurrence)
        .map(JSON.parse)
        // Panel-based recurrences...
        .filter((ids) => (ids.length === isPanel ? 3 : 2))
        // with ID matching `panelId`.
        .some(
          (ids) =>
            ids.at(0) === parseInt(sampleId) && ids.at(-1) === parseInt(id),
        )
    );
  }

  function createPayload(testPayload) {
    if (!isEnabled) return testPayload;
    return testPayload
      .map((payload) => {
        const {
          estimated_start_date: _,
          estimated_complete_date: __,
          ...test
        } = payload;
        const {
          sample_id: sampleId,
          assay_id: assayId,
          panel_id: panelId,
        } = test;
        const { dates } = getRecurrence({
          assayId,
          sampleId,
          ...(panelId && { panelId }),
        });

        return isEmpty(dates)
          ? payload
          : dates.map(({ start, end }) => ({
              ...test,
              ...(start && { estimated_start_date: start }),
              ...(end && { estimated_complete_date: end }),
            }));
      })
      .flat(2);
  }

  return {
    createPayload,
    checkEditing,
    getRecurrence,
    setRecurrence,
    removeRecurrence,
    recurrence: isEnabled ? recurrence : {},
  };
}
